/**
 * The modal dialog the site uses.
 *
 * Built on the native <dialog> element, which is the whole point: it brings the
 * focus trap, Escape, the backdrop and the top layer with it. Those four are
 * what hand written modals get wrong, and getting them wrong means a dialog a
 * keyboard cannot leave or one that renders behind the navbar.
 *
 * The shape is the one the rest of the site uses for a card: an icon chip and a
 * heading on one line, the text under it, the buttons on a footer of their own
 * with the primary one on the right where a desktop expects it. Left aligned,
 * because that is how everything else here reads.
 *
 * This layer knows nothing about where its options come from. It takes a title,
 * a body element and a list of buttons, and answers with the button that was
 * pressed. The translation from the option names the call sites use lives next
 * door in swal.js.
 */

const { SURFACE, BUTTON, TONES, el } = require('./style');

/**
 * m-auto is not decoration. Tailwind's reset sets margin: 0 on every element,
 * which takes out the margin: auto that the user agent uses to centre a modal
 * dialog in the top layer, and the dialog ends up in the top left corner.
 */
const SHELL = 'm-auto w-[calc(100%-2rem)] max-w-lg bg-transparent p-0 backdrop:bg-slate-900/70';

// A column, so that a dialog taller than the screen scrolls its middle and
// keeps the heading and the buttons where they are. The plugin scrolled the
// whole popup, buttons and all, which is how a confirm button ends up below
// the fold on a phone.
const PANEL = 'flex max-h-[85dvh] w-full flex-col overflow-hidden text-left ' + SURFACE;

let sequence = 0;

/**
 * Open a dialog.
 *
 * @param {Object} spec
 * @param {string} [spec.title]
 * @param {Node} [spec.icon] - the chip beside the title
 * @param {Node} [spec.body] - everything between the heading and the buttons.
 *   An element that already sits in the page is borrowed rather than taken: it
 *   goes back where it was when the dialog closes. Without that, a form the
 *   page renders once and shows in a dialog several times is in the document
 *   for the first open and gone for every one after, and the second open fails
 *   on a getElementById that used to work.
 * @param {Array<{id: string, label: string, tone?: string, autofocus?: boolean}>} [spec.buttons]
 *   A button is one of the three tones and nothing else: an arbitrary colour is
 *   how a codebase ends up with two reds that mean the same thing.
 *   In reading order, so the last one sits on the right.
 * @param {boolean|Function} [spec.dismissable=true] - whether a click on the
 *   backdrop closes it, and whether the corner button is offered at all.
 * @param {boolean|Function} [spec.escapable=true] - the same for Escape. Kept
 *   apart from the backdrop because they are different promises to the person
 *   using it: a dialog that ignores a stray click is fine, one that cannot be
 *   escaped is a trap.
 * @param {string} [spec.width]
 * @param {HTMLElement} [spec.focus] - what to focus instead of a button
 * @param {Function} [spec.onAction] - (id, controller) before closing. Return
 *   false, or a promise of false, to keep the dialog open. Anything else is
 *   handed back as the result value.
 * @param {Function} [spec.onOpen] - (controller) once it is on screen
 * @returns {Object} the controller
 */
function openDialog(spec) {
    const options = spec || {};
    const buttons = options.buttons || [{ id: 'confirm', label: 'OK', tone: 'primary', autofocus: true }];

    const dialog = el('dialog', SHELL);
    dialog.setAttribute('aria-modal', 'true');
    if (options.width) dialog.style.maxWidth = options.width;

    const panel = el('div', PANEL);
    dialog.appendChild(panel);

    // ── Head: chip, heading, and the way out ────────────────────────────────
    const head = el('div', 'flex shrink-0 items-start gap-3 px-6 pt-6');
    if (options.icon) head.appendChild(options.icon);

    const headText = el('div', 'min-w-0 flex-1');
    if (options.title) {
        sequence += 1;
        const heading = el('h2', 'text-base font-semibold text-slate-900 dark:text-white', options.title);
        heading.id = 'dialog-title-' + sequence;
        dialog.setAttribute('aria-labelledby', heading.id);
        headText.appendChild(heading);
    }
    head.appendChild(headText);

    const dismiss = el('button', 'js-dialog-dismiss -mr-2 -mt-2 flex h-8 w-8 shrink-0 items-center justify-center '
        + 'rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 '
        + 'dark:hover:bg-gray-700 dark:hover:text-slate-200');
    dismiss.type = 'button';
    dismiss.setAttribute('aria-label', 'Close');
    dismiss.appendChild(el('i', 'fa-solid fa-xmark'));
    head.appendChild(dismiss);
    panel.appendChild(head);

    // ── Body ────────────────────────────────────────────────────────────────
    // ui-scroll is in app.css: a scrollbar in the theme's colours rather than
    // the platform's, which on a dark panel means a light bar in the dark.
    const content = el('div', 'ui-scroll overflow-y-auto px-6 pb-2 pt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300');
    if (options.icon) content.className += ' sm:pl-[4.25rem]'; // line up under the heading

    // Where a borrowed body has to be put back, see the note on spec.body.
    const bodyHome = options.body && options.body.parentNode ? options.body.parentNode : null;
    if (options.body) content.appendChild(options.body);
    panel.appendChild(content);

    // Hidden until something is wrong, and announced when it appears: a message
    // that only changes colour is no message at all for a screen reader.
    const validation = el('p', 'mx-6 mb-2 mt-2 hidden shrink-0 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 '
        + 'dark:bg-red-900/20 dark:text-red-300');
    validation.setAttribute('role', 'alert');
    panel.appendChild(validation);

    // Only for the dialogs that work without a button to spin inside.
    const spinner = el('div', 'hidden items-center gap-2 px-6 pb-2 text-sm text-slate-500 dark:text-slate-400');
    spinner.appendChild(el('i', 'fa-solid fa-circle-notch fa-spin'));
    spinner.appendChild(el('span', '', 'Working...'));
    panel.appendChild(spinner);

    // ── Footer ──────────────────────────────────────────────────────────────
    const footer = el('div', 'mt-4 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 '
        + 'bg-slate-50 px-6 py-4 dark:border-white/10 dark:bg-gray-900/40');
    panel.appendChild(footer);

    const byId = {};
    let settle = null;
    let closed = false;
    let loading = false;

    const controller = {
        element: dialog,
        panel,
        body: content,
        result: new Promise(resolve => { settle = resolve; }),

        /** @returns {HTMLElement|null} */
        button(id) { return byId[id] || null; },

        /**
         * Everything off while something is in flight. The spinner goes inside
         * the primary button when there is one, so the dialog does not change
         * size at the moment the person is waiting on it.
         */
        setLoading(on) {
            loading = Boolean(on);
            Object.keys(byId).forEach(id => { byId[id].disabled = loading; });
            dismiss.disabled = loading;

            const primary = byId.confirm || byId[Object.keys(byId)[0]];
            if (primary) {
                // Built with the button and only shown, so nothing has to be
                // inserted into a live dialog.
                primary.spinner.style.display = loading ? '' : 'none';
            } else {
                spinner.classList.toggle('hidden', !loading);
                spinner.classList.toggle('flex', loading);
            }
        },

        isLoading() { return loading; },

        /** @param {string|null} message */
        setValidation(message) {
            if (message) {
                validation.textContent = message;
                validation.classList.remove('hidden');
            } else {
                validation.textContent = '';
                validation.classList.add('hidden');
            }
        },

        hasValidation() { return !validation.classList.contains('hidden'); },

        /**
         * @param {string|null} action - the button id, or null when nothing was pressed
         * @param {Object} [extra] - { reason, value }
         */
        close(action, extra) {
            if (closed) return;
            closed = true;

            // Before the dialog is taken apart, or the body goes with it.
            if (bodyHome && options.body) bodyHome.appendChild(options.body);

            if (typeof dialog.close === 'function' && dialog.open) dialog.close();
            if (dialog.parentNode) dialog.parentNode.removeChild(dialog);

            settle({
                action: action || null,
                reason: (extra && extra.reason) || (action ? 'action' : 'dismiss'),
                value: extra ? extra.value : undefined
            });
        }
    };

    /**
     * @param {string} name - 'dismissable' or 'escapable'
     * @returns {boolean}
     */
    function allows(name) {
        const value = options[name];
        if (typeof value === 'function') return Boolean(value());
        return value !== false;
    }

    buttons.forEach(button => {
        const node = el('button', BUTTON + ' ' + (TONES[button.tone] || TONES.neutral));
        node.type = 'button';
        node.dataset.action = button.id;
        // Hidden with an inline style rather than the hidden class. Font
        // Awesome loads after Tailwind and sets display on .fa-solid at the
        // same specificity, so .hidden loses and every button would sit there
        // spinning. An inline style beats both stylesheets and cannot lose that
        // argument later either.
        node.spinner = el('i', 'fa-solid fa-circle-notch fa-spin');
        node.spinner.style.display = 'none';
        node.appendChild(node.spinner);
        node.appendChild(el('span', '', button.label));

        node.addEventListener('click', function () {
            if (loading) return;
            controller.setValidation(null);

            if (typeof options.onAction !== 'function') {
                controller.close(button.id);
                return;
            }

            const outcome = options.onAction(button.id, controller);
            Promise.resolve(outcome).then(function (value) {
                // False keeps it open, and so does a validation message the
                // handler put up on its way out: both are ways of saying "not
                // like that", and call sites use each of them.
                if (value === false || controller.hasValidation()) {
                    controller.setLoading(false);
                    return;
                }
                controller.close(button.id, { value });
            });
        });

        byId[button.id] = node;
        footer.appendChild(node);
    });

    // The corner button is a dismissal, the same as Escape. It is hidden rather
    // than removed when a dialog refuses to be dismissed, so the heading keeps
    // the same width either way.
    dismiss.classList.toggle('hidden', !allows('dismissable') && !allows('escapable'));
    dismiss.addEventListener('click', function () {
        if (!loading) controller.close(null, { reason: 'close' });
    });

    // Escape. The native event is cancelable, which is the only way to refuse a
    // dismissal without taking the key over entirely.
    dialog.addEventListener('cancel', function (event) {
        event.preventDefault();
        if (!loading && allows('escapable')) controller.close(null, { reason: 'esc' });
    });

    // A click on the backdrop reaches the dialog itself, never the panel.
    dialog.addEventListener('click', function (event) {
        if (event.target !== dialog) return;
        if (!loading && allows('dismissable')) controller.close(null, { reason: 'backdrop' });
    });

    document.body.appendChild(dialog);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', 'open'); // stubs and very old browsers

    const marked = buttons.find(b => b.autofocus);
    const target = options.focus || (marked && byId[marked.id]);
    if (target && typeof target.focus === 'function') target.focus();

    if (typeof options.onOpen === 'function') options.onOpen(controller);

    return controller;
}

module.exports = { openDialog, el };
